import { useMemo, useState } from 'react';
import { css } from '@emotion/react';
import {
  DeviceType_Enum,
  LineKey,
  LineKeyType_Enum,
  ListDevice,
  ModelInfo,
} from '@weave/schema-gen-ts/dist/schemas/phone/devices/v2/devices.pb';
import { DevicesQueries } from '@frontend/api-devices';
import { LineKeysApi } from '@frontend/api-line-keys';
import { Trans, useTranslation } from '@frontend/i18n';
import { usePhoneSettingsShallowStore } from '@frontend/phone-config';
import { useQuery } from '@frontend/react-query-helpers';
import { genUUIDV4 } from '@frontend/string';
import { theme } from '@frontend/theme';
import { CheckboxField, Modal, ModalControlModalProps, Text, useFormField } from '@frontend/design-system';
import { queryKeys } from '../../../../query-keys';
import { PreviewLayoutModal } from '../preview-layout/preview-only-layout';
import { useLineKeyContext } from '../store/line-key-provider';
import { useLineKeyState } from '../store/line-key-provider';
import { ExistingLayoutDevicesTable } from '../tables/use-existing-layout-table';
import { OverrideConfirmationModal } from './override-confirm.modal';

type Props = ModalControlModalProps & {
  device: ListDevice | undefined;
};

export const UseExistingLayoutModal = ({ device, ...rest }: Props) => {
  const { t } = useTranslation('phone', { keyPrefix: 'line-keys' });
  const { globalAvailableLocationIds: locationIds, settingsTenantLocation } = usePhoneSettingsShallowStore(
    'globalAvailableLocationIds',
    'settingsTenantLocation'
  );
  const locationId = locationIds?.[0] ?? '';
  const tenantId = settingsTenantLocation?.phoneTenantId ?? '';
  const { deviceId } = device ?? {};
  const [previewDevice, setPreviewDevice] = useState<ListDevice>();
  const [showOverrideConfirmationModal, setShowOverrideConfirmationModal] = useState(false);
  const { finalLineKeys, setFinalLineKeys } = useLineKeyState(['finalLineKeys', 'setFinalLineKeys']);
  const { lineKeys: destinationLineKeys } = useLineKeyContext();
  const hideOverrideConfirmation = localStorage.getItem(`hide-override-confirmation-${locationId}`);
  const [filteredLocations, setFilteredLocations] = useState<string[]>([]);

  const { data: models, isLoading: _isLoadingModels } = useQuery({
    queryKey: [tenantId, ...queryKeys.settings.listDeviceModels()],
    queryFn: () => LineKeysApi.GetDeviceModelInfo(),
    select: ({ models }) => {
      return models.reduce((acc, modelInfo) => {
        return {
          ...acc,
          [modelInfo.model]: modelInfo,
        };
      }, {} as Record<string, ModelInfo>);
    },
  });

  const { data: devices } = DevicesQueries.useGetDevicesList(
    {
      locationIds: filteredLocations.length ? filteredLocations : locationIds,
      deviceType: DeviceType_Enum.DESK_PHONE,
      tenantId: settingsTenantLocation?.phoneTenantId,
    },
    {
      select: (data) => {
        return data.devices.filter((device) => {
          return device.deviceId !== deviceId;
        });
      },
    }
  );

  const filteredDevices = devices?.filter(
    (device) =>
      !device.make ||
      (!models?.[device.model] && device.make) ||
      !!models?.[device.model].sideKeys ||
      !!models?.[device.model].touchKeys
  );

  const model = models?.[previewDevice?.model ?? ''];

  const { data: computedLineKeys } = useQuery({
    queryKey: queryKeys.settings.computeAdaptedLineKeys({
      tenantId,
      srcDeviceId: previewDevice?.deviceId ?? '',
      dstDeviceId: deviceId ?? '',
    }),
    queryFn: () =>
      LineKeysApi.ComputeAdaptedLineKeys({ srcDeviceId: previewDevice?.deviceId ?? '', dstDeviceId: deviceId ?? '' }),
    enabled: !!previewDevice?.deviceId && !!deviceId,
  });

  // Fetch the selected source device layout so we can preserve source voicemail keys
  // when ComputeAdaptedLineKeys omits voicemail entries.
  const { data: sourceLineKeysResponse } = useQuery({
    queryKey: [tenantId, previewDevice?.deviceId ?? '', ...queryKeys.settings.listLineKeys()],
    queryFn: () => LineKeysApi.GetLineKeys(previewDevice?.deviceId ?? ''),
    enabled: !!previewDevice?.deviceId,
    meta: {
      error: {
        message: 'UseExistingLayoutModal:sourceLineKeysQuery | Failed to fetch source device line keys',
        data: {
          tenantId,
          sourceDeviceId: previewDevice?.deviceId ?? '',
          destinationDeviceId: deviceId ?? '',
          locationId,
        },
      },
    },
  });

  // Use adapted keys when complete; otherwise rebuild from source order and keep
  // voicemail keys in their original slots so preview/save do not drop them.
  const adaptedLineKeysWithVoicemailFallback = useMemo(() => {
    const adaptedLineKeys = computedLineKeys?.lineKeys ?? [];
    const currentLineKeys = Object.values(finalLineKeys ?? destinationLineKeys ?? {});
    const sourceLineKeys = sourceLineKeysResponse?.lineKeys ?? [];

    if (!adaptedLineKeys.length) {
      return currentLineKeys;
    }

    const isVoicemailLineKey = (lineKey: LineKey) =>
      lineKey.lineKeyType === LineKeyType_Enum.VOICEMAIL_EXTENSION ||
      lineKey.lineKeyType === LineKeyType_Enum.PERSONAL_VOICEMAIL;

    if (adaptedLineKeys.some(isVoicemailLineKey)) {
      return adaptedLineKeys;
    }

    if (!sourceLineKeys.length) {
      return adaptedLineKeys;
    }

    const adaptedQueue = [...adaptedLineKeys];
    const mergedLineKeys = sourceLineKeys.map((lineKey) =>
      isVoicemailLineKey(lineKey) ? lineKey : adaptedQueue.shift() ?? lineKey
    );

    return [...mergedLineKeys, ...adaptedQueue];
  }, [computedLineKeys?.lineKeys, finalLineKeys, destinationLineKeys, sourceLineKeysResponse?.lineKeys]);

  const handleClose = () => {
    setPreviewDevice(undefined);
    setShowOverrideConfirmationModal(false);
    rest.onClose();
  };

  const handleSave = () => {
    if (!!fieldProps.value) localStorage.setItem(`hide-override-confirmation-line-keys`, 'true');
    setFinalLineKeys(
      adaptedLineKeysWithVoicemailFallback.reduce((acc, lineKey) => {
        return {
          ...acc,
          [genUUIDV4()]: lineKey,
        };
      }, {})
    );
    handleClose();
  };

  const fieldProps = useFormField({
    type: 'checkbox',
    value: false,
  });

  if (showOverrideConfirmationModal && !hideOverrideConfirmation) {
    return (
      <Modal {...rest} maxWidth={600}>
        <OverrideConfirmationModal
          onClose={handleClose}
          setShowOverrideConfirmationModal={setShowOverrideConfirmationModal}
          onSave={handleSave}
        >
          <Text
            css={css`
              margin-bottom: ${theme.spacing(2)};
            `}
          >
            {t('Using another device’s Line Key layout will override the current layout of this device.')}
          </Text>
          <Text
            css={css`
              margin-bottom: ${theme.spacing(2)};
            `}
          >
            <Trans t={t} deviceName={device?.name} previewDeviceName={previewDevice?.name}>
              <Text as='span'>
                Are you sure you want to apply{' '}
                <Text as='span' weight='bold'>
                  {{ previewDeviceName: previewDevice?.name }}
                </Text>{' '}
                Line Key layout to
              </Text>{' '}
              <Text as='span' weight='bold'>
                {{ deviceName: device?.name }}
              </Text>
              ?
            </Trans>
          </Text>
          <CheckboxField {...fieldProps} name='validate-override-box' label={t("Don't ask me again")}></CheckboxField>
        </OverrideConfirmationModal>
      </Modal>
    );
  }

  if (previewDevice) {
    return (
      <Modal {...rest} maxWidth={800}>
        <PreviewLayoutModal
          model={model}
          lineKeys={adaptedLineKeysWithVoicemailFallback}
          onBack={() => setPreviewDevice(undefined)}
          onClose={handleClose}
          device={previewDevice}
        >
          <Modal.Actions
            secondaryLabel={t('Cancel')}
            onSecondaryClick={handleClose}
            primaryLabel={t('Use this Layout')}
            onPrimaryClick={() => {
              if (hideOverrideConfirmation) handleSave();
              else setShowOverrideConfirmationModal(true);
            }}
            primaryTrackingId='duplicate-use-this-layout'
          />
        </PreviewLayoutModal>
      </Modal>
    );
  }

  return (
    <Modal {...rest} maxWidth={800}>
      <ExistingLayoutDevicesTable
        setPreviewDevice={setPreviewDevice}
        device={device}
        models={models}
        devices={filteredDevices ?? []}
        filteredLocations={filteredLocations}
        setFilteredLocations={setFilteredLocations}
        {...rest}
      />
    </Modal>
  );
};
